import * as React from 'react';

import { Button, Col, EntityDetails, Row } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  LucideArchive,
  LucideInfo,
  LucideLock,
  LucideLockOpen,
} from 'lucide-react';

import {
  lockInventoryPeriodAtom,
  unlockInventoryPeriodAtom,
  archiveInventoryPeriodAtom,
} from '../../_graphql/mutationInventoryPeriod';
import * as state from '../../_state/periodDetail.state';
import { useTranslations } from '../../i18n/useTranslations';

import { PeriodBalanceTable } from './PeriodBalanceTable';
import { PeriodLockDialog } from './PeriodLockDialog';
import { PeriodStatusBadge } from './PeriodStatusBadge';
import { PeriodUnlockDialog } from './PeriodUnlockDialog';

import { useModuleLocation } from '~/shared/hooks/useModuleLocation';
import {
  formatExclusiveEndDateShort,
  formatZonedDateTime,
  formatZonedDateTimeShort,
} from '~/shared/utils/dateTime';

type PeriodInfoRowProps = {
  label: string;
  value: React.ReactNode;
};

const PeriodInfoRow = ({ label, value }: PeriodInfoRowProps) => (
  <Row className="@PeriodInfoRow py-2">
    <span className="w-40 shrink-0 text-sm text-neutral/50">{label}</span>
    <span className="text-sm">{value}</span>
  </Row>
);

export const InventoryPeriodDetailView = () => {
  const t = useTranslations();
  const toast = useToast();
  const [, setLocation] = useModuleLocation();

  const [isUnlockDialogOpen, setIsUnlockDialogOpen] = React.useState(false);

  const {
    data: period,
    isFetching,
    loadingState,
    error,
    refetch,
  } = useAtomValue(state.$remotePeriodDetail);

  const [isLockDialogOpen, setIsLockDialogOpen] = useAtom(
    state.$isLockDialogOpen,
  );

  const lockPeriod = useSetAtom(lockInventoryPeriodAtom({}));
  const unlockPeriod = useSetAtom(unlockInventoryPeriodAtom({}));
  const archivePeriod = useSetAtom(archiveInventoryPeriodAtom({}));

  const handleBack = React.useCallback(() => {
    setLocation('/inventory/periods');
  }, [setLocation]);

  const handleLockConfirm = React.useCallback(async () => {
    if (!period) return;
    try {
      await lockPeriod({ id: period.id });
      toast.success(t.periodsListView.actions.lockSuccess);
      setIsLockDialogOpen(false);
      refetch();
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : t.periodsListView.actions.unknownError;
      toast.error(t.periodsListView.actions.lockError(message));
    }
  }, [period, lockPeriod, toast, t, refetch, setIsLockDialogOpen]);

  const handleUnlockConfirm = React.useCallback(
    async (reason: string, expiresAt?: string) => {
      if (!period) return;
      try {
        await unlockPeriod({
          id: period.id,
          reason,
          expiresAt,
        });
        toast.success(t.periodsListView.actions.unlockSuccess);
        setIsUnlockDialogOpen(false);
        refetch();
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : t.periodsListView.actions.unknownError;
        toast.error(t.periodsListView.actions.unlockError(message));
      }
    },
    [period, unlockPeriod, toast, t, refetch],
  );

  const handleArchive = React.useCallback(async () => {
    if (!period) return;
    try {
      await archivePeriod({ id: period.id });
      toast.success(t.periodsListView.actions.archiveSuccess);
      refetch();
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : t.periodsListView.actions.unknownError;
      toast.error(t.periodsListView.actions.archiveError(message));
    }
  }, [period, archivePeriod, toast, t, refetch]);

  const headerActions = React.useMemo(() => {
    if (!period) return null;

    const actions: React.ReactNode[] = [];

    if (period.status === 'Closed') {
      actions.push(
        <Button
          key="lock"
          icon={<LucideLock />}
          color="primary"
          onPress={() => setIsLockDialogOpen(true)}
        >
          {t.periodsListView.actions.lock}
        </Button>,
      );
    }

    if (period.status === 'Locked') {
      actions.push(
        <Button
          key="unlock"
          icon={<LucideLockOpen />}
          variant="outline"
          color="neutral"
          onPress={() => setIsUnlockDialogOpen(true)}
        >
          {t.periodsListView.actions.unlock}
        </Button>,
      );
      actions.push(
        <Button
          key="archive"
          icon={<LucideArchive />}
          color="primary"
          onPress={handleArchive}
        >
          {t.periodsListView.actions.archive}
        </Button>,
      );
    }

    if (actions.length === 0) return null;

    return <Row gap2>{actions}</Row>;
  }, [period, t, handleArchive, setIsLockDialogOpen]);

  return (
    <>
      <EntityDetails
        className="@InventoryPeriodDetailView"
        error={error}
        loading={loadingState.isInitialLoading || isFetching}
        onBackPressed={handleBack}
        primaryTitle={
          period ? t.periodsListView.detail.title(period.name) : 'Loading...'
        }
        badges={period ? <PeriodStatusBadge status={period.status} /> : null}
        subtitle={
          period ? (
            <div className="flex flex-wrap items-center gap-x-1">
              <span className="font-medium">{period.facilityName}</span>
              <span className="text-neutral/30">&middot;</span>
              <span>
                {t.periodsListView.periodConfig.periodTypeOptions[
                  period.periodType
                ] ?? period.periodType}
              </span>
            </div>
          ) : undefined
        }
        headerActions={headerActions}
        tabs={{
          overview: {
            title: t.periodsListView.detail.overview,
            content: () =>
              period ? (
                <Col className="p-6" gap4>
                  <Col gap2>
                    <h3 className="text-base font-semibold">
                      {t.periodsListView.detail.periodInfo}
                    </h3>
                    <Col className="rounded-lg border border-neutral/10 p-4">
                      <PeriodInfoRow
                        label={t.periodsListView.detail.fields.name}
                        value={period.name}
                      />
                      <PeriodInfoRow
                        label={t.periodsListView.detail.fields.facility}
                        value={period.facilityName}
                      />
                      <PeriodInfoRow
                        label={t.periodsListView.detail.fields.periodType}
                        value={
                          t.periodsListView.periodConfig.periodTypeOptions[
                            period.periodType
                          ] ?? period.periodType
                        }
                      />
                      <PeriodInfoRow
                        label={t.periodsListView.detail.fields.startDate}
                        value={formatZonedDateTimeShort(period.startDate)}
                      />
                      <PeriodInfoRow
                        label={t.periodsListView.detail.fields.endDate}
                        value={formatExclusiveEndDateShort(period.endDate)}
                      />
                      <PeriodInfoRow
                        label={t.periodsListView.detail.fields.status}
                        value={<PeriodStatusBadge status={period.status} />}
                      />
                      {period.closedAt && (
                        <PeriodInfoRow
                          label={t.periodsListView.detail.fields.closedAt}
                          value={formatZonedDateTime(period.closedAt, {
                            includeTime: true,
                          })}
                        />
                      )}
                      {period.lockedAt && (
                        <PeriodInfoRow
                          label={t.periodsListView.detail.fields.lockedAt}
                          value={formatZonedDateTime(period.lockedAt, {
                            includeTime: true,
                          })}
                        />
                      )}
                      {period.unlockReason && (
                        <PeriodInfoRow
                          label={t.periodsListView.detail.fields.unlockReason}
                          value={period.unlockReason}
                        />
                      )}
                      {period.notes && (
                        <PeriodInfoRow
                          label={t.periodsListView.detail.fields.notes}
                          value={period.notes}
                        />
                      )}
                      <PeriodInfoRow
                        label={t.periodsListView.detail.fields.createdAt}
                        value={formatZonedDateTime(period.createdAt, {
                          includeTime: true,
                        })}
                      />
                      <PeriodInfoRow
                        label={t.periodsListView.detail.fields.updatedAt}
                        value={formatZonedDateTime(period.updatedAt, {
                          includeTime: true,
                        })}
                      />
                    </Col>
                  </Col>
                  {period.status === 'Open' && (
                    <Row className="items-center gap-2 rounded-lg bg-primary/5 px-4 py-3">
                      <LucideInfo className="size-4 shrink-0 text-primary" />
                      <span className="text-sm text-primary">
                        {t.periodsListView.detail.autoCloseInfo(
                          formatZonedDateTimeShort(period.endDate),
                        )}
                      </span>
                    </Row>
                  )}
                  {period.unlockExpiresAt && (
                    <Row className="items-center gap-2 rounded-lg bg-primary/5 px-4 py-3">
                      <LucideInfo className="size-4 shrink-0 text-primary" />
                      <span className="text-sm text-primary">
                        {t.periodsListView.detail.autoRelockInfo(
                          formatZonedDateTime(period.unlockExpiresAt, {
                            includeTime: true,
                          }),
                        )}
                      </span>
                    </Row>
                  )}
                  {period.status !== 'Open' && <PeriodBalanceTable />}
                </Col>
              ) : null,
          },
        }}
        currentTab="overview"
        onChangeTab={() => void 0}
      />

      {period && (
        <>
          <PeriodLockDialog
            periodName={period.name}
            isOpen={isLockDialogOpen}
            onClose={() => setIsLockDialogOpen(false)}
            onConfirm={handleLockConfirm}
          />
          <PeriodUnlockDialog
            periodName={period.name}
            isOpen={isUnlockDialogOpen}
            onClose={() => setIsUnlockDialogOpen(false)}
            onConfirm={handleUnlockConfirm}
          />
        </>
      )}
    </>
  );
};
